import { menuData, type MenuItem } from '@/config/menu'
import { usePermissions } from '@/composables/usePermissions'
import { useProfileStore } from '@/stores/profile'

/**
 * Middleware global de sécurité :
 * Empêche un utilisateur d'accéder à une page qui ne correspond à aucun
 * item de menu autorisé (permissions / groupes) en manipulant directement l'URL.
 *
 * - On ne touche pas aux routes d'auth (/auth/**).
 * - SuperAdmin garde toujours l'accès complet.
 * - On ne bloque que si l'URL correspond à un item de menu EXISTANT
 *   mais NON autorisé pour l'utilisateur courant.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  // Laisser passer l'auth et la page « aucun rôle »
  if (to.path.startsWith('/auth')) {
    return
  }

  // Attendre que le profil soit chargé avant de vérifier les permissions
  const profileStore = useProfileStore()
  
  // Côté serveur, laisser passer (le middleware auth gère déjà)
  if (!process.client) {
    return
  }
  
  // Côté client, attendre que le profil soit chargé
  if (!profileStore.profile) {
    try {
      await profileStore.fetchProfile()
    } catch (error) {
      // Si le profil ne peut pas être chargé, laisser passer (l'auth middleware gérera)
      console.warn('Impossible de charger le profil dans check-menu-access:', error)
      return
    }
  }

  // Si le profil n'est toujours pas chargé après l'attente, laisser passer
  if (!profileStore.profile) {
    return
  }

  // Normaliser le chemin (sans slash final)
  const currentPath = to.path !== '/' && to.path.endsWith('/')
    ? to.path.slice(0, -1)
    : to.path

  const { isSuperAdmin, hasPermission, hasAnyRole, hasAnyGroup, currentGroups } = usePermissions()

  // Forcer l'évaluation de currentGroups pour s'assurer que le mapping est fait
  const userGroups = currentGroups.value

  // Super admin : accès complet
  if (isSuperAdmin.value) {
    return
  }

  const { homePath, hasVisibleMenu, isLandingPath } = useHomePath()

  // Compte sans aucun menu : toujours la page d'erreur, quelle que soit l'URL
  if (!hasVisibleMenu()) {
    return navigateTo('/auth/acces-refuse', { replace: true })
  }

  if (isLandingPath(currentPath)) {
    return navigateTo(homePath(), { replace: true })
  }

  // Aplatir le menu pour rechercher un item correspondant au chemin
  const flatMenu: MenuItem[] = []

  const flatten = (items: MenuItem[]) => {
    items.forEach((item) => {
      // Ne pousser que les items qui ont un `to` (les parents sans `to` ne doivent pas bloquer)
      if (item.to) {
        flatMenu.push(item)
      }
      if (item.children && item.children.length > 0) {
        flatten(item.children)
      }
    })
  }

  flatten(menuData)

  // Cas spécial AVANT la vérification du menu : Les étudiants doivent pouvoir accéder à leur propre fiche
  // même si "Liste des étudiants" les exclut
  const { isStudent } = usePermissions()
  const isStudentAccessingOwnProfile = (): boolean => {
    if (!isStudent.value) return false
    
    // Vérifier si le chemin est une fiche étudiant : /admin/academy/students/{slug}
    const studentDetailPattern = /^\/admin\/academy\/students\/[^/]+$/
    if (!studentDetailPattern.test(currentPath)) return false
    
    // Extraire le slug de l'URL
    const pathParts = currentPath.split('/').filter(Boolean)
    const slugFromUrl = pathParts[pathParts.length - 1]
    
    // Vérifier si c'est le slug de l'étudiant connecté
    const studentSlug = (profileStore.profile as any)?.etudiant?.slug
    return slugFromUrl === studentSlug
  }
  
  // Si c'est un étudiant accédant à sa propre fiche, autoriser immédiatement
  if (isStudentAccessingOwnProfile()) {
    return
  }

  // Chercher un item de menu dont le chemin correspond au chemin courant
  // Priorité aux correspondances exactes, puis aux préfixes
  // Exemple: item.to = '/infrastructure', currentPath = '/admin/infrastructure'
  // (mapPath ajoutera le préfixe, donc on fait une comparaison souple)
  const matchingItems = flatMenu.filter((item) => {
    if (!item.to) return false
    // On autorise un match si le chemin courant correspond exactement ou commence par le chemin du menu
    return currentPath === item.to || currentPath.startsWith(item.to + '/')
  })
  
  // Si aucune correspondance dans le menu, on ne bloque pas :
  // il peut s'agir d'une page technique ou d'un détail non listé.
  if (matchingItems.length === 0) {
    return
  }

  // Reprise de la logique d'autorisation de useMenu.isItemAllowed
  const isItemAllowed = (item: MenuItem): boolean => {
    // Certains items peuvent être masqués explicitement pour le super admin
    if (item.has_restriction_for_admin && isSuperAdmin.value) {
      return false
    }

    const conds: boolean[] = []

    if (item.requirePermissionsAny?.length) {
      conds.push(item.requirePermissionsAny.some((a) => hasPermission(a)))
    }
    if (item.requireRolesAny?.length) {
      conds.push(hasAnyRole(item.requireRolesAny))
    }
    if (item.requireGroupsAny?.length) {
      conds.push(hasAnyGroup(item.requireGroupsAny))
    }
    if (item.isVisible) {
      conds.push(
        item.isVisible({
          hasPermission,
          hasAnyRole,
          hasAnyGroup,
          isSuperAdmin: isSuperAdmin.value,
        })
      )
    }

    return conds.length === 0 ? true : conds.some(Boolean)
  }

  // Vérifier si l'utilisateur a accès à AU MOINS UN des items correspondants
  // Priorité aux correspondances exactes
  const exactMatches = matchingItems.filter(item => item.to === currentPath)
  const itemsToCheck = exactMatches.length > 0 ? exactMatches : matchingItems
  
  // Trier par longueur de chemin (plus long = plus spécifique = priorité)
  itemsToCheck.sort((a, b) => (b.to?.length || 0) - (a.to?.length || 0))
  
  // Vérifier si l'utilisateur a accès à au moins un des items
  const allowed = itemsToCheck.some(item => isItemAllowed(item))

  // Si l'item de menu existe mais n'est PAS autorisé pour cet utilisateur,
  // on bloque l'accès à la route même si l'utilisateur a manipulé l'URL.
  if (!allowed) {
    return navigateTo(homePath(), { replace: true })
  }
}
)



